/**
 * Lead mutation hooks using generic factory
 * Significantly reduced boilerplate while maintaining full type safety
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { leadApi } from '@/lib/api/leads.api';
import { queryKeys } from '../query-keys';
import { createResourceMutations } from './createResourceMutations';
import { leadModuleCountsKey } from '../useLeadModuleCounts';
import { toast } from 'sonner';
import type { Lead, CreateLead, UpdateLead } from '@scrambl/shared';

/**
 * Generate all lead mutation hooks using the factory
 */
const mutations = createResourceMutations<Lead, CreateLead, UpdateLead>({
  resourceName: 'Lead',
  resourceNameSingular: 'lead',
  queryKeyFactory: queryKeys.leads,
  api: {
    create: (data: CreateLead) => leadApi.createLead(data),
    update: (id: string, data: UpdateLead) => leadApi.updateLead(id, data),
    updateField: (id: string, field: string, value: unknown) => {
      const updateData = { [field]: value } as UpdateLead;
      return leadApi.updateLead(id, updateData);
    },
    delete: (id: string) => leadApi.deleteLead(id),
  },
  enableRealtime: false, // Leads don't need realtime for now
  deferredEntityType: 'lead',
  invalidation: {
    onCreate: { invalidateRelated: [[...leadModuleCountsKey]] },
    onDelete: { invalidateRelated: [[...leadModuleCountsKey]] },
  },
});

/**
 * Export individual hooks with original names for backward compatibility
 */
export const useCreateLeadMutation = mutations.useCreateMutation;
export const useUpdateLeadMutation = mutations.useUpdateMutation;
export const useUpdateLeadFieldMutation = mutations.useUpdateFieldMutation!;
export const useDeleteLeadMutation = mutations.useDeleteMutation;

/**
 * Bulk delete leads mutation hook
 */
export function useBulkDeleteLeadsMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (ids: string[]) => leadApi.bulkDeleteLeads(ids),
    onSuccess: (result) => {
      toast.success(`${result.deletedCount} lead${result.deletedCount === 1 ? '' : 's'} deleted`);
      // Invalidate leads list and module counts
      queryClient.invalidateQueries({ queryKey: queryKeys.leads.lists() });
      queryClient.invalidateQueries({ queryKey: leadModuleCountsKey });
    },
    onError: (error) => {
      toast.error('Failed to delete leads');
      console.error('Bulk delete leads error:', error);
    },
  });
}
